import {
	ReactElement,
	useCallback,
	useState
} from "react";
import { ContentBlock } from "../PublicPage/ContentBlock";
import {
	SingleSignOnSettingsViewModel,
	SingleSignOnType
} from "client/Api/SingleSignOnSettings/singleSignOnSettings";
import { createRoot } from "react-dom/client";
import {
	BusyIndicator,
	Form,
	FormItem,
	Toolbar
} from "client/Components";
import {
	SingleSignOnFormData,
	SingleSignOnSettingsControl
} from "../Settings/SsoSettings/SingleSignOnSettingsControl";
import {
	convertToFormData,
	useSingleSignOnSettingsForm
} from "../Settings/SsoSettings/useSingleSignOnSettingsForm";
import { IFormHandler, useFormHandler } from "client/Forms";
import { ssoSettingsValidationConfiguration } from "../Settings/SsoSettings/validation";
import { navigateRelativeToEnvironment } from "client_legacy/ndaw/navigator";
import { localize } from "client_legacy/ndaw/culture";
import { navigateToWorkspace } from "../Workspace/routing";
import { ensureNotNil } from "client/Misc/utils";

export type ConfigureSingleSignOnProps = {
	viewModel: SingleSignOnSettingsViewModel;
};

export const ConfigureSingleSignOn = (props: ConfigureSingleSignOnProps): ReactElement => {
	const {
		viewModel
	} = props;

	const isCustomSignOutRedirectUrlConfigurable: boolean = false;

	const [isBusy, setIsBusy] = useState<boolean>(false);

	const [initialFormData] = useState<SingleSignOnFormData>(
		(): SingleSignOnFormData => {
			return convertToFormData(
				viewModel,
				isCustomSignOutRedirectUrlConfigurable
			);
		}
	);

	const [formData, setFormData] = useState<SingleSignOnFormData>(initialFormData);

	const formHandler: IFormHandler<SingleSignOnFormData> = useFormHandler(
		formData,
		setFormData,
		ssoSettingsValidationConfiguration
	);

	const [
		submitHandler,
		verifyOAuth2Handler,
		enableMicrosoftAuthenticatorMobileSignInHandler,
		hasUnsavedOAuth2CredentialsChanges,
		formDialog
	] = useSingleSignOnSettingsForm(
		formHandler,
		initialFormData,
		isBusy,
		setIsBusy
	);

	const doneHandler = useCallback(
		async (): Promise<void> => {
			const result: TryResult<SingleSignOnType> = await submitHandler();

			if (!result.wasSuccessful) {
				return;
			}

			const selectedSingleSignOnType: SingleSignOnType = result.result;

			if (selectedSingleSignOnType === SingleSignOnType.OAuth2
				|| selectedSingleSignOnType === SingleSignOnType.WsFederation
			) {
				navigateRelativeToEnvironment("SignOut?skipSignOutFromExternalProvider=true");
			} else {
				navigateToWorkspace();
			}
		},
		[submitHandler]
	);

	return (
		<>
			<ContentBlock size="large">
				<h1>{localize("_ConfigureSingleSignOn")}</h1>

				{isBusy && (
					<BusyIndicator />
				)}

				<Form onSubmit={doneHandler}>
					<SingleSignOnSettingsControl
						formHandler={formHandler}
						availableSingleSignOnTypes={viewModel.availableSingleSignOnTypes}
						signoutUrlHelpLink={viewModel.signOutHelpUrl}
						configurationHelpLink={viewModel.configurationHelpUrl}
						activeDirectoryHostingLocation={viewModel.activeDirectoryHostingLocation}
						hasCredentialsForCurrentUser={viewModel.hasCredentialsForCurrentUser}
						isAutomaticSetupAvailable={viewModel.isAutomaticSetupAvailable}
						productName={viewModel.productName}
						onVerifyOAuth2={verifyOAuth2Handler}
						onEnableMicrosoftAuthenticatorMobileSignIn={enableMicrosoftAuthenticatorMobileSignInHandler}
						hasUnsavedOAuth2CredentialsChanges={hasUnsavedOAuth2CredentialsChanges}
					/>

					<FormItem>
						<Toolbar horizontalAlignment="left">
							<Toolbar.Button
								kind="primary"
								text={localize("_Ok")}
								trackingId="ok"
								onClick={doneHandler}
							/>
						</Toolbar>
					</FormItem>
				</Form>
			</ContentBlock>

			{formDialog}
		</>
	);
};
ConfigureSingleSignOn.displayName = "ConfigureSingleSignOn";

export function init(
	viewModel: SingleSignOnSettingsViewModel
): void {
	createRoot(
		ensureNotNil(
			document.getElementById("configure-single-sign-on-page")
		)
	)
		.render(
			<ConfigureSingleSignOn viewModel={viewModel} />
		);
}
